import { MinTableKey } from "./table";

export const FEEDBACK_STATUS = {
  NEW: "new",
  REVIEWED: "reviewed",
  ACTIONED: "actioned",
  APPROVED: "approved", // go-ahead (Sam's, or the release manager's) to merge an actioned branch
  INFRA_HOLD: "infra-hold", // approved, but the branch changes serverless/CI/scripts — blocked until Sam acks it
  MERGED: "merged",
  RELEASED: "released", // live on prod — Sam sets this by hand after his prod deploy; users see "Ready to try"
  DECLINED: "declined",
} as const;
export type FeedbackStatus =
  (typeof FEEDBACK_STATUS)[keyof typeof FEEDBACK_STATUS];

// Where an item came from, when it didn't come from a person typing into the
// feedback widget. Set only on writes made with the agent API key — a user
// login can never set it, so it can't be forged by submitting feedback. It
// decides how much the triage agent trusts the text: only "release-manager"
// items are direction; everything else is data (see feedback-agent.sh).
export const FEEDBACK_ORIGIN = {
  RELEASE_MANAGER: "release-manager", // a spec written by the release manager (trusted by the triage agent)
  MERGE_REVIEWER: "merge-reviewer", // an improvement idea the pre-merge reviewer left behind
  PROD: "prod", // copied from prod feedback — public-user text, strictly data
} as const;
export type FeedbackOrigin = (typeof FEEDBACK_ORIGIN)[keyof typeof FEEDBACK_ORIGIN];

export const FEEDBACK_PLATFORMS = ["ios", "android", "windows", "macos", "linux", "unknown"] as const;
export type FeedbackPlatform = (typeof FEEDBACK_PLATFORMS)[number];
export const FEEDBACK_DEVICE_TYPES = ["mobile", "tablet", "desktop", "unknown"] as const;
export type FeedbackDeviceType = (typeof FEEDBACK_DEVICE_TYPES)[number];

// Where the feedback was sent from. Client-reported, so informational only.
export type FeedbackContext = {
  userAgent?: string; // navigator.userAgent, truncated
  platform: FeedbackPlatform; // derived from the UA; "unknown" when uncertain
  deviceType: FeedbackDeviceType; // "unknown" when neither UA nor screen settles it
  screen?: { width: number; height: number }; // device screen, to infer orientation/size
  viewport?: { width: number; height: number };
};

export type FeedbackData = {
  type: "feedback";
  id: string;
  userId: string; // the submitter (an attribute, and the GSI2 partition — feedback itself is keyed by id alone)
  text: string;
  page?: string;
  context?: FeedbackContext;
  status: FeedbackStatus;
  resolutionNote?: string;
  adminNote?: string; // admin's clarification/correction, set when sending an item back to the agent; the agent clears it (to "") once handled
  branch?: string; // set by the triage agent when it opens work on this item
  mergedAt?: string; // set by merge-approved.sh when it moves the item to merged
  mergeCommit?: string; // the merge commit on main, set alongside mergedAt — what a release is tagged from
  origin?: FeedbackOrigin; // absent = a person submitted it
  releaseId?: string; // the release this item was queued for by the release manager
  releaseOrder?: number; // build order within that release (lowest first)
  previousReleaseIds?: string[]; // releases this item was moved out of (oldest first), kept for reference when the release manager re-homes it
  prodRef?: string; // for origin "prod": the prod feedback id this was copied from
  // merge-approved.sh holds branches that touch serverless.yml / CI / scripts
  // until Sam acks them: it records which files and the branch tip it saw,
  // and an ack only counts if the tip hasn't moved since (infraAck is
  // human-only — the API refuses it from the agent key).
  infraFiles?: string[];
  infraHoldSha?: string;
  infraAck?: boolean;
  createdAt: string;
  updatedAt: string;
};

// GSI1 (sparse — only feedback items set these) lets a single Query answer
// "all feedback across every user", which the base table's per-user
// partitioning can't do on its own. GSI1SK is `{status}#{createdAt}#{id}` so
// `begins_with(GSI1SK, "new#")` fetches only unreviewed items directly, and
// items stay date-ordered within a status.
export type FeedbackDBData = MinTableKey & FeedbackData;

// What a regular user sees of their own feedback: a quiet, coarse status.
// "On the way" is merged (on dev); "Ready to try" is the plain "released"
// status, which Sam sets by hand once the change is actually on prod — a
// tagged release only means the tag was pushed, not that prod has it, and
// prod's table has no release records to derive it from anyway.
export type MyFeedbackStatus = "New" | "Pending" | "On the way" | "Ready to try";

export type MyFeedbackItem = {
  id: string;
  text: string;
  createdAt: string;
  status: MyFeedbackStatus;
};

export const toMyFeedbackStatus = (status: FeedbackStatus): MyFeedbackStatus =>
  status === FEEDBACK_STATUS.NEW
    ? "New"
    : status === FEEDBACK_STATUS.MERGED
      ? "On the way"
      : status === FEEDBACK_STATUS.RELEASED
        ? "Ready to try"
        : "Pending";
